import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import {
  HumanTaskIdleResponseAction,
  HumanTaskIdleResponseRequest,
  HumanTaskIdleResponseResponse,
  HumanTaskIdleStatus,
  formatSilentDuration,
} from '../humanTaskIdle';
import './idle-nudge-banner.scss';

interface IdleNudgeBannerProps {
  taskId: string;
  status: string;
  idleStatus: HumanTaskIdleStatus | null | undefined;
  /** Signed-in operator holds the task. Others (admins) can only release. */
  isHolder: boolean;
  /** Refetch the task after the operator has answered. */
  onResponded: () => void;
}

type Choice = HumanTaskIdleResponseAction | null;

/** The server refused because the task is no longer held by this operator. */
const isNotHeldStatus = (httpStatus: number | undefined) =>
  httpStatus === 409 || httpStatus === 403;

/**
 * Forced-choice check-in shown in the operator's task thread once an accepted
 * task has gone quiet: still on it (with a one-line status the requester's
 * assistant can relay), blocked (closes the task with a reason), or release
 * back to the queue with a handoff note. Nothing here is user-facing copy.
 */
const IdleNudgeBanner: React.FC<IdleNudgeBannerProps> = ({
  taskId,
  status,
  idleStatus,
  isHolder,
  onResponded,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [choice, setChoice] = useState<Choice>(null);
  const [note, setNote] = useState('');
  const [timeSpent, setTimeSpent] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notHeldNotice, setNotHeldNotice] = useState<string | null>(null);

  useEffect(() => {
    setChoice(null);
    setNote('');
    setTimeSpent('');
    setError(null);
    setNotHeldNotice(null);
  }, [taskId]);

  const show =
    status === 'accepted' &&
    !!idleStatus &&
    (idleStatus.severity !== 'ok' || idleStatus.nudgedAt !== null);

  if (!show || !idleStatus) {
    if (!notHeldNotice) return null;
    return (
      <div
        className='idle-nudge idle-nudge--not-held'
        role='status'
        data-testid='idle-nudge-not-held'
      >
        <div className='idle-nudge-heading'>
          This task is no longer held by you
        </div>
        <div className='idle-nudge-sub'>
          Your last answer was not recorded ({notHeldNotice}).
        </div>
        <div className='idle-nudge-actions'>
          <button
            type='button'
            className='idle-nudge-btn idle-nudge-btn--link'
            onClick={() => setNotHeldNotice(null)}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  const submit = async (action: HumanTaskIdleResponseAction) => {
    const trimmed = note.trim();
    if (action !== 'release' && !trimmed) {
      setError(
        action === 'still_working'
          ? 'Add a one-line status so the requester knows where things stand.'
          : 'Say what is blocking the task.',
      );
      return;
    }
    if (action === 'blocked' && (timeSpent === '' || timeSpent < 1)) {
      setError('Enter the minutes you spent (at least 1).');
      return;
    }
    const body: HumanTaskIdleResponseRequest = {
      action,
      note: trimmed || undefined,
      timeSpentMinutes: action === 'blocked' ? Number(timeSpent) : undefined,
    };
    try {
      setSubmitting(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await api.post<HumanTaskIdleResponseResponse>(
        `/human-tasks/${taskId}/idle-response`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.data.success) {
        setError(response.data.error || 'Could not record your response');
        return;
      }
      setChoice(null);
      setNote('');
      onResponded();
    } catch (err: any) {
      console.error('Error answering idle check-in:', err);
      const message =
        err.response?.data?.error || 'Could not record your response';
      setError(message);
      if (isNotHeldStatus(err.response?.status)) {
        setNotHeldNotice(message);
        onResponded();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const silent = formatSilentDuration(idleStatus.silentMinutes);
  const heading =
    idleStatus.severity === 'stale'
      ? `No activity for ${silent} — this task will be handed back if left unanswered`
      : `No activity on this task for ${silent}`;

  return (
    <div
      className={`idle-nudge idle-nudge--${idleStatus.severity}`}
      role='alert'
      data-testid='idle-nudge-banner'
    >
      <div className='idle-nudge-heading'>{heading}</div>
      <div className='idle-nudge-sub'>
        {isHolder
          ? "Still on it, blocked, or should someone else pick it up? Pick one so the requester isn't left waiting."
          : 'Only the holder can post a status or close it as blocked; you can hand it back to the queue.'}
      </div>

      {choice === null ? (
        <div className='idle-nudge-actions'>
          {isHolder && (
            <>
              <button
                type='button'
                className='idle-nudge-btn idle-nudge-btn--primary'
                onClick={() => setChoice('still_working')}
              >
                Still on it
              </button>
              <button
                type='button'
                className='idle-nudge-btn'
                onClick={() => setChoice('blocked')}
              >
                Blocked / can't do
              </button>
            </>
          )}
          <button
            type='button'
            className='idle-nudge-btn'
            onClick={() => setChoice('release')}
          >
            Release to queue
          </button>
        </div>
      ) : (
        <form
          className='idle-nudge-form'
          onSubmit={(e) => {
            e.preventDefault();
            submit(choice);
          }}
        >
          <label className='idle-nudge-label'>
            {choice === 'still_working' && 'Where are you up to? (one line)'}
            {choice === 'blocked' && "What's blocking it?"}
            {choice === 'release' &&
              'Handoff note for the next operator (optional)'}
            <textarea
              className='idle-nudge-note'
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                choice === 'still_working'
                  ? 'e.g. On hold with the clinic, third attempt'
                  : choice === 'blocked'
                    ? 'e.g. Site rejects every submission — account looks flagged'
                    : 'e.g. Tried twice, no answer; best to call after 2pm'
              }
              autoFocus
            />
          </label>
          {choice === 'blocked' && (
            <label className='idle-nudge-label idle-nudge-label--inline'>
              Time spent (minutes)
              <input
                className='idle-nudge-minutes'
                type='number'
                min={1}
                value={timeSpent}
                onChange={(e) =>
                  setTimeSpent(
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
              />
            </label>
          )}
          <div className='idle-nudge-actions'>
            <button
              type='submit'
              className='idle-nudge-btn idle-nudge-btn--primary'
              disabled={submitting}
            >
              {submitting
                ? 'Saving…'
                : choice === 'still_working'
                  ? 'Post status'
                  : choice === 'blocked'
                    ? 'Close as blocked'
                    : 'Release'}
            </button>
            <button
              type='button'
              className='idle-nudge-btn idle-nudge-btn--link'
              disabled={submitting}
              onClick={() => {
                setChoice(null);
                setError(null);
              }}
            >
              Back
            </button>
          </div>
        </form>
      )}

      {error && <div className='idle-nudge-error'>{error}</div>}
    </div>
  );
};

export default IdleNudgeBanner;
